import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import axios from "axios";
import Swal from "sweetalert2";
import ActionsMenu from "./ActionsMenu";

export default function ModuleTabs() {
  const user = JSON.parse(sessionStorage.getItem("user"));

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    module_id: "",
    tab_code: "",
    tab_name: "",
    route_path: "",
    display_order: 1,
    is_active: true,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ------------------------------------------------
  // MODULES
  // ------------------------------------------------

  const fetchModules = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/rbacall/modules/all/${clientId}/${collegeId}`
      );

      if (data.success) {
        setModules(data.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ------------------------------------------------
  // TABS
  // ------------------------------------------------

  const fetchData = async () => {
    setLoading(true);

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/rbacall/module-tabs/${clientId}/${collegeId}`,
        {
          params: {
            page: page + 1,
            pageSize,
            search,
          },
        }
      );

      if (data.success) {
        setRows(
          (data.data?.items || []).map((row) => ({
            id: row.module_tab_id,
            ...row,
          }))
        );

        setRowCount(data.data?.total || 0);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to load tabs",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId && collegeId) {
      fetchModules();
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    if (clientId && collegeId) {
      fetchData();
    }

    // eslint-disable-next-line
  }, [clientId, collegeId, page, pageSize, search]);

  // ------------------------------------------------
  // OPEN
  // ------------------------------------------------

  const handleOpen = () => {
    setEditing(null);

    setForm({
      module_id: "",
      tab_code: "",
      tab_name: "",
      route_path: "",
      display_order: 1,
      is_active: true,
    });

    setOpen(true);
  };

  const handleEdit = (row) => {
    setEditing(row);

    setForm({
      module_id: row.module_id || "",
      tab_code: row.tab_code || "",
      tab_name: row.tab_name || "",
      route_path: row.route_path || "",
      display_order: row.display_order || 1,
      is_active: Boolean(row.is_active),
    });

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // ------------------------------------------------
  // SAVE
  // ------------------------------------------------

  const handleSubmit = async () => {
    try {
      if (!form.module_id) {
        setSnackbar({
          open: true,
          message: "Please select module",
          severity: "warning",
        });

        return;
      }

      if (!form.tab_name.trim()) {
        setSnackbar({
          open: true,
          message: "Tab name is required",
          severity: "warning",
        });

        return;
      }

      if (editing) {
        await axios.put(
          `${API_BASE_URL}/api/rbacall/module-tabs/${clientId}/${collegeId}/${editing.module_tab_id}`,
          {
            ...form,
            modified_by: user?.user_email || "system",
          }
        );

        setSnackbar({
          open: true,
          message: "Tab updated successfully",
          severity: "success",
        });
      } else {
        await axios.post(`${API_BASE_URL}/api/rbacall/module-tabs/${clientId}/${collegeId}`, {
          ...form,
          created_by: user?.user_email || "system",
        });

        setSnackbar({
          open: true,
          message: "Tab created successfully",
          severity: "success",
        });
      }

      setOpen(false);

      fetchData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Save failed",
        severity: "error",
      });
    }
  };

  // ------------------------------------------------
  // DELETE
  // ------------------------------------------------

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This tab will be deactivated.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/rbacall/module-tabs/${clientId}/${collegeId}/${row.module_tab_id}`
      );

      await Swal.fire("Deleted!", "Tab deactivated successfully.", "success");

      fetchData();
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "Delete failed", "error");
    }
  };

  // ------------------------------------------------
  // COLUMNS
  // ------------------------------------------------

  const columns = [
    {
      field: "serial",
      headerName: "S. No.",
      width: 80,
      renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },

    {
      field: "module_name",
      headerName: "Module",
      flex: 1,
    },

    {
      field: "tab_code",
      headerName: "Tab Code",
      flex: 0.8,
    },

    {
      field: "tab_name",
      headerName: "Tab Name",
      flex: 1,
    },

    {
      field: "route_path",
      headerName: "Route",
      flex: 1,
    },

    {
      field: "display_order",
      headerName: "Order",
      width: 90,
    },

    {
      field: "is_active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (params.value === true ? "Active" : "Inactive"),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <ActionsMenu row={params.row} onEdit={handleEdit} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            Module Tabs
          </Typography>

          <TextField
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{ color: "#fff" }}
          >
            Add Tab
          </Button>
        </Toolbar>

        <Box mt={2}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Module Tab" : "Add Module Tab"}</DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Module"
              value={form.module_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  module_id: e.target.value,
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select Module</option>

              {modules.map((module) => (
                <option key={module.module_id} value={module.module_id}>
                  {module.module_name}
                </option>
              ))}
            </TextField>

            <TextField
              label="Tab Code"
              value={form.tab_code}
              onChange={(e) =>
                setForm({
                  ...form,
                  tab_code: e.target.value.toUpperCase(),
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Tab Name"
              value={form.tab_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  tab_name: e.target.value,
                })
              }
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Route Path"
              value={form.route_path}
              onChange={(e) =>
                setForm({
                  ...form,
                  route_path: e.target.value,
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Display Order"
              type="number"
              value={form.display_order}
              onChange={(e) =>
                setForm({
                  ...form,
                  display_order: Number(e.target.value),
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              select
              label="Status"
              value={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: Number(e.target.value),
                })
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option value={true}>Active</option>

              <option value={false}>Inactive</option>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>

          <Button variant="contained" onClick={handleSubmit} sx={{ color: "#fff" }}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar({
              ...snackbar,
              open: false,
            })
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
